import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useLocation } from 'react-router-dom';
import { formatReadableAmount } from '../../../../Common/FormatFunctions/FormatAmount';
import { RingLoader } from 'react-spinners';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, TableFooter } from "@mui/material";
import { FormaDateBalanceSheet } from '../../../../Common/FormatFunctions/FormatDate';
import PdfPreview from '../../../../Common/PdfPreview_Default';

const apikey = process.env.REACT_APP_API;

const PattiEntryRegister = () => {
    const location = useLocation();
    const Company_Name = sessionStorage.getItem('Company_Name')
    const Company_GSTNO = sessionStorage.getItem('Company_GSTNO')
    const searchParams = new URLSearchParams(location.search);
    const fromDate = searchParams.get('fromDate');
    const toDate = searchParams.get('toDate');
    const company_Code = searchParams.get('Company_Code');
    const YearCode = searchParams.get('Year_Code');
    const acCode = searchParams.get('acCode');
    const [pdfPreview, setPdfPreview] = useState(null);

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [grandTotals, setGrandTotals] = useState({
        netAmt: 0,
        Hamaliamt: 0,
        Amount: 0,
        Tolai: 0,
        Patti_Amount: 0,
        netqntl: 0
    });

    const API_URL = `${apikey}/Patti_RegisterReport`;

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear());
        return `${day}/${month}/${year}`;
    };

    useEffect(() => {
        const fetchReportData = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await axios.get(API_URL, {
                    params: {
                        fromDate: fromDate,
                        toDate: toDate,
                        Company_Code: company_Code,
                        Year_Code: YearCode
                    },
                });
                setReportData(response.data);
            } catch (error) {
                console.error('Error fetching report:', error);
                setError('Error fetching report');
            } finally {
                setLoading(false);
            }
        };

        fetchReportData();
    }, [API_URL]);

    useEffect(() => {
        if (reportData.length > 0) {
            const totals = reportData.reduce(
                (acc, item) => {
                    acc.netAmt += Number(item.netAmt) || 0;
                    acc.Hamaliamt += Number(item.Hamaliamt) || 0;
                    acc.Amount += Number(item.Amount) || 0;
                    acc.Tolai += Number(item.Tolai) || 0;
                    acc.Patti_Amount += Number(item.Patti_Amount) || 0;
                    acc.netqntl += Number(item.NETQNTL) || 0;
                    return acc;
                },
                { netAmt: 0, Hamaliamt: 0, Amount: 0, Tolai: 0, Patti_Amount: 0, netqntl: 0 }
            );

            setGrandTotals(totals);
        }
    }, [reportData]);

    const handleExportToExcel = () => {
        const wb = XLSX.utils.book_new();
        const headers = [
            "Patti No",	"Patti Date", "Farmer Name", "Net Amount", "Hamali", "Tolai","Patti Amount",
        ];

        const formattedData = reportData.map(item => ({
            "Patti No": item.Doc_No,
            "Patti Date": formatDate(item.Doc_Date),
            "Farmer Name": item.FarmerName,
            "Net Amount": Number(item.netAmt) || 0,
            "Hamali": Number(item.Hamaliamt) || 0,
            "Tolai": Number(item.Tolai) || 0,
            "Patti Amount": Number(item.Patti_Amount) || 0,
        }));
        const ws = XLSX.utils.json_to_sheet(formattedData, { header: headers });
        const wsCols = [
            { wch: 12, alignment: { horizontal: "right" } },
            { wch: 10, alignment: { horizontal: "right" } },
            { wch: 15, alignment: { horizontal: "right" } },
            { wch: 12, alignment: { horizontal: "right" } },
            { wch: 12, alignment: { horizontal: "right" } },
            { wch: 12, alignment: { horizontal: "right" } },
            { wch: 15, alignment: { horizontal: "right" } },
        ];

        ws["!cols"] = wsCols;
        XLSX.utils.book_append_sheet(wb, ws, 'PattiRegister');
        XLSX.writeFile(wb, 'PattiRegister.xlsx');
    };

    const handlePrint = () => {
        const companyName = Company_Name;
        const fromDateParam = searchParams.get('fromDate');
        const toDateParam = searchParams.get('toDate');

        const doc = generatePDFDocument(companyName, fromDateParam, toDateParam);
        const pdfUrl = URL.createObjectURL(doc);
        const printWindow = window.open(pdfUrl);
        printWindow.onload = function () {
            setTimeout(() => {
                printWindow.print();
            }, 500);
        };
    };

    const generatePDFDocument = (companyName, fromDate, toDate) => {
        const doc = new jsPDF();

        // Add header information
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(companyName, doc.internal.pageSize.width / 2, 15, { align: 'center' });

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`GSTN : ${Company_GSTNO}`, doc.internal.pageSize.width / 2, 23, { align: 'center' });

        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text("Patti Register", doc.internal.pageSize.width / 2, 32, { align: 'center' });

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`${FormaDateBalanceSheet(fromDate)} to ${FormaDateBalanceSheet(toDate)}`, doc.internal.pageSize.width / 2, 40, { align: 'center' });

        const startY = 45;

        // Prepare table data
        const tableData = [
            [
                "Patti No",	"Patti Date", "Farmer Name", "Net Amount", "Hamali", "Tolai","Patti Amount",
            ],
            ...reportData.map(item => [
                item.Doc_No,
                formatDate(item.Doc_Date),
                item.FarmerName,
                formatReadableAmount(item.netAmt),
                formatReadableAmount(item.Hamaliamt),
                formatReadableAmount(item.Tolai),
                formatReadableAmount(item.Patti_Amount),
            ]),
            [
                '', '', 'Total',
                formatReadableAmount(grandTotals.netAmt),
                formatReadableAmount(grandTotals.Hamaliamt),
                formatReadableAmount(grandTotals.Tolai),
                formatReadableAmount(grandTotals.Patti_Amount),
        
            ]
        ];

        // Add table to PDF
        doc.autoTable({
            head: [tableData[0]],
            body: tableData.slice(1, -1),
            foot: [tableData[tableData.length - 1]],
            startY: startY,
            styles: {
                fontSize: 8,
                cellPadding: 2,
                halign: 'center',
            },
            headStyles: {
                fillColor: [255, 0, 0],
                textColor: [255, 255, 255],
                fontStyle: 'bold',
            },
            footStyles: {
                fillColor: [255, 255, 0],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                halign: 'right',
            },
            columnStyles: {
                2: { halign: 'left' },
                4: { halign: 'right' },
                5: { halign: 'right' },
                6: { halign: 'right' },
                7: { halign: 'right' },
                8: { halign: 'right' },
                9: { halign: 'right' },
                10: { halign: 'right' },
            },
            margin: { top: startY },
        });

        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        setPdfPreview(pdfUrl);
        return pdfBlob;
    };

    const groupReportData = (data) => {
        const groupedData = {};
        data.forEach((item) => {
            const key = `${item.Id}`;
            if (!groupedData[key]) {
                groupedData[key] = {
                    items: [],
                    totalQty: 0,
                };
            }
            groupedData[key].items.push(item);
            groupedData[key].totalQty += parseFloat(item.Bill_Amount) || 0;
        });
        return groupedData;
    };

    const groupedReportData = groupReportData(reportData);

    return (
        <div style={{ marginTop: "10px" }}>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "24px", fontWeight: "bold" }}>{Company_Name}</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px", textDecoration: 'underline', fontWeight: "550" }}>GSTN : {Company_GSTNO}</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "20px", fontWeight: "bold" }}>Patti Register</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px" }}>{FormaDateBalanceSheet(fromDate)} to {FormaDateBalanceSheet(toDate)}</Typography>

            <div className="mb-3 row align-items-center">
                <div className="col-auto">
                    <button className="btn btn-secondary me-2" onClick={handlePrint}>
                        Print
                    </button>
                    <button className="btn btn-success" onClick={handleExportToExcel}>
                        Export to Excel
                    </button>
                    <button className="btn btn-secondary me-2" onClick={() => generatePDFDocument(Company_Name, fromDate, toDate)}>
                        Preview PDF
                    </button>
                    {pdfPreview && (
                        <PdfPreview
                            pdfData={pdfPreview}
                            apiData={reportData[0]}
                            label={"PattiRegister"}
                        />
                    )}
                </div>
            </div>

            <div style={{
                maxHeight: 'calc(100vh - 200px)',
                overflow: 'auto',
                position: 'relative',
                border: '1px solid #e0e0e0',
                marginBottom: '60px'
            }} id="reportTable">
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: 'white'
                    }}>
                        <TableRow>
                            <TableCell style={{ textAlign: "center", fontWeight: "bold", backgroundColor: 'white' }}>Patti No</TableCell>
                            <TableCell style={{ textAlign: "center", fontWeight: "bold", backgroundColor: 'white' }}>Patti Date</TableCell>
                            <TableCell style={{ textAlign: "center", fontWeight: "bold", backgroundColor: 'white' }}>Farmer Name</TableCell>
                            <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>Net Amount</TableCell>
                            {/* <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>Advance</TableCell> */}
                            <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>Hamali</TableCell>
                            <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>Tolai</TableCell>
                            <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>Patti Amount</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {Object.entries(groupedReportData).map(([key, { items, totalQty }]) => {
                            return (
                                <React.Fragment key={key}>
                                    {items.map((item, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                cursor: "pointer",
                                                "&:hover": {
                                                    backgroundColor: "#fdfd96",
                                                },
                                            }}
                                        >
                                            <TableCell style={{ textAlign: "center" }}>{item.Doc_No}</TableCell>
                                            <TableCell style={{ textAlign: "center" }}>{formatDate(item.Doc_Date)}</TableCell>
                                            <TableCell style={{ textAlign: "center" }}>{item.FarmerName}</TableCell>
                                            <TableCell style={{ textAlign: "right" }}>
                                                {formatReadableAmount(item.netAmt)}
                                            </TableCell>

                                            {/* <TableCell style={{ textAlign: "right" }}>
                                                {formatReadableAmount(item.Amount)}
                                            </TableCell> */}
                                            <TableCell style={{ textAlign: "right" }}>
                                                {formatReadableAmount(item.Hamaliamt)}
                                            </TableCell>
                                            <TableCell style={{ textAlign: "right" }}>
                                                {formatReadableAmount(item.Tolai)}
                                            </TableCell>
                                            <TableCell style={{ textAlign: "right" }}>
                                                {formatReadableAmount(item.Patti_Amount)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>

                    <TableFooter style={{ position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 1 }}>
                        <TableRow style={{ backgroundColor: "yellow" }}>
                            <TableCell colSpan={2} className="fw-bold" style={{ fontWeight: "bold", fontSize: "14px" }}>Total</TableCell>
                            <TableCell className="fw-bold"></TableCell>
                            <TableCell className="fw-bold text-end" style={{ fontWeight: "bold", fontSize: "14px" }}>
                                {formatReadableAmount(grandTotals.netAmt)}
                            </TableCell>
                            {/* <TableCell className="fw-bold text-end" style={{ fontWeight: "bold", fontSize: "14px" }}>{formatReadableAmount(grandTotals.Amount)}</TableCell> */}
                            <TableCell className="fw-bold text-end" style={{ fontWeight: "bold", fontSize: "14px" }}>{formatReadableAmount(grandTotals.Hamaliamt)}</TableCell>
                            <TableCell className="fw-bold text-end" style={{ fontWeight: "bold", fontSize: "14px" }}>{formatReadableAmount(grandTotals.Tolai)}</TableCell>
                            <TableCell className="fw-bold text-end" style={{ fontWeight: "bold", fontSize: "14px" }}>{formatReadableAmount(grandTotals.Patti_Amount)}</TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </div>

            {loading && (
                <div style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 9999
                }}>
                    <RingLoader size={80} />
                </div>
            )}
            {error && <div className="alert alert-danger">{error}</div>}
        </div>
    );
};

export default PattiEntryRegister;
